import { MouseEventHandler } from 'react'
import { Typography } from '../../text'
import { ButtonBase, styled } from '@mui/material'
import { tokens } from 'ComponentLibrary'
import { Link as RouterLink } from 'react-router-dom'
import { Plus } from '@phosphor-icons/react'

type CreationButtonContentProps = {
    label: string
    color: 'royal' | 'blue' | 'turquoise' | 'green' | 'leaf' | 'lime' | 'orange' | 'red' | 'grape' | 'purple'
}

type CreationButtonProps = {
    onClick: MouseEventHandler<HTMLButtonElement>
} & CreationButtonContentProps

type CreationNavButtonProps = {
    href: string
} & CreationButtonContentProps

function CreationButtonContent({ label, color }: CreationButtonContentProps) {
    return (
        <CreationButtonContainer $color={color}>
            <IconContainer $color={color}>
                <Plus size={tokens.SizingIconM} color={CREATION_BUTTON_COLORS[color].color} />
            </IconContainer>
            <Typography variant="link" color={tokens.ColorTextDefault} noWrap>
                {label}
            </Typography>
        </CreationButtonContainer>
    )
}

const CreationButtonContainer = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $color: CreationButtonContentProps['color'] }>(({ $color }) => ({
    display: 'flex',
    gap: tokens.SizingSpacing100,
    alignItems: 'center',
    borderRadius: tokens.SizingCornerL,
    outline: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
    paddingLeft: tokens.SizingSpacing100,
    paddingRight: tokens.SizingSpacing200,
    paddingTop: tokens.SizingSpacing100,
    paddingBottom: tokens.SizingSpacing100,
    backgroundColor: tokens.ColorSurfaceDefault,
    '&:hover': {
        backgroundColor: CREATION_BUTTON_COLORS[$color].backgroundColor,
    },
}))

const IconContainer = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $color: CreationButtonContentProps['color'] }>(({ $color }) => ({
    width: '40px',
    height: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: CREATION_BUTTON_COLORS[$color].backgroundColor,
    borderRadius: tokens.SizingCornerM,
}))

export function CreationButton({ label, onClick, color }: CreationButtonProps) {
    return (
        <ButtonBase disableRipple onClick={onClick}>
            <CreationButtonContent label={label} color={color} />
        </ButtonBase>
    )
}

export function CreationNavButton({ label, href, color }: CreationNavButtonProps) {
    return (
        <ButtonBase disableRipple component={RouterLink} to={href}>
            <CreationButtonContent label={label} color={color} />
        </ButtonBase>
    )
}

const CREATION_BUTTON_COLORS: Record<CreationButtonContentProps['color'], { backgroundColor: string; color: string }> = {
    royal: {
        backgroundColor: tokens.ColorGraphSequentialOneLightest,
        color: tokens.ColorGraphSequentialOneMedium,
    },
    blue: {
        backgroundColor: tokens.ColorGraphSequentialTwoLightest,
        color: tokens.ColorGraphSequentialTwoMedium,
    },
    turquoise: {
        backgroundColor: tokens.ColorGraphSequentialThreeLightest,
        color: tokens.ColorGraphSequentialThreeMedium,
    },
    green: {
        backgroundColor: tokens.ColorGraphSequentialFourLightest,
        color: tokens.ColorGraphSequentialFourMedium,
    },
    leaf: {
        backgroundColor: tokens.ColorGraphSequentialFiveLightest,
        color: tokens.ColorGraphSequentialFiveMedium,
    },
    lime: {
        backgroundColor: tokens.ColorGraphSequentialSixLightest,
        color: tokens.ColorGraphSequentialSixMedium,
    },
    orange: {
        backgroundColor: tokens.ColorGraphSequentialSevenLightest,
        color: tokens.ColorGraphSequentialSevenMedium,
    },
    red: {
        backgroundColor: tokens.ColorGraphSequentialEightLightest,
        color: tokens.ColorGraphSequentialEightMedium,
    },
    grape: {
        backgroundColor: tokens.ColorGraphSequentialNineLightest,
        color: tokens.ColorGraphSequentialNineMedium,
    },
    purple: {
        backgroundColor: tokens.ColorGraphSequentialTenLightest,
        color: tokens.ColorGraphSequentialTenMedium,
    },
}
